import type { ExpressionSpecification, LayerSpecification, Map as MapLibreMap } from 'maplibre-gl';

// App locales whose code differs from the tiles' `name:<code>` fields, with a
// second language for places that have no name in the first one.
const MAP_LANGUAGES: Record<string, Array<string>> = { bre: ['br', 'fr'] };

/** The `name:<code>` fields to try, in order, before the place's own name. */
export function resolveMapLanguages(locale: string): Array<string> {
  return MAP_LANGUAGES[locale] ?? [locale];
}

export function localizedName(locale: string): ExpressionSpecification {
  const translations = resolveMapLanguages(locale).map((code): ExpressionSpecification => [
    'get',
    `name:${code}`,
  ]);
  return ['coalesce', ...translations, ['get', 'name']];
}

// Matches `name`, `name:fr` and `name_en`, not `ref`: road numbers stay as they are.
function readsName(textField: unknown): boolean {
  return /"name([:_]|")/.test(JSON.stringify(textField ?? null));
}

/**
 * The style's own label layers, those that write a place name. Picked by what
 * they print rather than by id, which differs from one style to another; our
 * layers print no text and are left alone.
 */
export function nameLayerIds(layers: Array<LayerSpecification>): Array<string> {
  return layers
    .filter(layer => layer.type === 'symbol' && readsName(layer.layout?.['text-field']))
    .map(layer => layer.id);
}

/** Rewrites the labels in place: the GTFS and vehicle layers stay, unlike with a style reload. */
export function localizeMapLabels(map: MapLibreMap, locale: string): void {
  const textField = localizedName(locale);
  nameLayerIds(map.getStyle().layers).forEach(layerId =>
    map.setLayoutProperty(layerId, 'text-field', textField),
  );
}
